import axios, { AxiosError } from 'axios';
import { useCarnetStore } from '@/store/carnetStore';
import type { CodigoCarnet, PerfilCarnet, SesionCarnet } from '@/types/carnet';

/**
 * Cliente propio del portal del empleado.
 *
 * No reusa `apiClient` a propósito: aquel inyecta el token del panel y ante un
 * 401 redirige a `/login`. Un empleado que llega ahí no tiene usuario ni clave
 * que escribir — su camino de reingreso es el botón de Google en `/mi-carnet`.
 */
const baseURL = (import.meta.env.VITE_API_BASE_URL?.toString() ?? '') + '/api/v1';

export const carnetClient = axios.create({
  baseURL: baseURL || '/api/v1',
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

carnetClient.interceptors.request.use((config) => {
  const token = useCarnetStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

carnetClient.interceptors.response.use(
  (res) => res,
  (error: AxiosError) => {
    const status = error.response?.status;
    const esLogin = (error.config?.url ?? '').includes('/carnet/auth/');

    // 401 → la sesión de 30 días venció, o Safari purgó el almacenamiento tras
    // varios días sin visitas. Se limpia para que la pantalla ofrezca entrar
    // de nuevo con Google en vez de quedarse en un error opaco.
    if (status === 401 && !esLogin) {
      useCarnetStore.getState().cerrarSesion();
    }

    return Promise.reject(error);
  },
);

export const carnetApi = {
  /** Cambia el ID token de Google por una sesión propia del portal. */
  loginGoogle: (idToken: string) =>
    carnetClient
      .post<SesionCarnet>('/carnet/auth/google', { id_token: idToken })
      .then((r) => r.data),

  perfil: () => carnetClient.get<PerfilCarnet>('/carnet/perfil').then((r) => r.data),

  codigo: () => carnetClient.get<CodigoCarnet>('/carnet/codigo').then((r) => r.data),

  rotarSemilla: () =>
    carnetClient.post<{ rotada_en: string }>('/carnet/rotar-semilla').then((r) => r.data),
};

/** Mensaje que le sirve al empleado, no el stack del servidor. */
export function mensajeErrorCarnet(err: unknown, fallback = 'No pudimos cargar tu carnet'): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string | string[] } | undefined;
    if (Array.isArray(data?.message)) return data.message.join(', ');
    if (data?.message) return data.message;
    if (!err.response) return 'Sin conexión. Revisa tu señal e inténtalo de nuevo.';
  }
  return fallback;
}
